import React, { useMemo, useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import AdminGuestDetailView from '../../pages/admin/AdminGuestDetailView';
import AdminHostDetailView from '../../pages/admin/AdminHostDetailView';
import { formatEuropeanDateTime } from '../../utils/dateFormat';
import { Badge, Card, TimelinePreview } from './AdminPanelPrimitives';
import { toggleSelectableFilters } from './AdminGeoMapExplorer';

const getUserResults = (searchQuery, searchResults, users) => (searchQuery ? searchResults : users);

const accountFilterOptions = [
  { id: 'all', label: 'All' },
  { id: 'guest', label: 'Guest' },
  { id: 'host', label: 'Host' },
  { id: 'staff', label: 'Staff' },
  { id: 'owner', label: 'Owners' },
  { id: 'admin', label: 'Admin' },
  { id: 'deleted', label: 'Deleted' },
];

const isDeletedAccount = (user) => Boolean(user?.deleted) || Boolean(user?.deletion_pending);
const isFinalisedDeletion = (user) => Boolean(user?.deleted);

export const AdminAccountsPanel = ({
  guestDetail,
  guestDetailLoading,
  hostDetail,
  hostDetailLoading,
  recentSearches,
  searchQuery,
  searchResults,
  selectedGuest,
  selectedHost,
  setActiveTab,
  setGuestDetail,
  setHostDetail,
  setSearchQuery,
  setSelectedGuest,
  setSelectedHost,
  setSelectedVenueApp,
  users,
  venueApps,
  expandedTimelineId,
  setExpandedTimelineId,
  onDeleteReview,
  onDeleteUser,
  onContactUser,
  onFetchGuestDetail,
  onFetchHostDetail,
  onRemoveQr,
  onSaveRecentSearch,
  onStartConversation,
}) => {
  const [selectedRoleFilters, setSelectedRoleFilters] = useState(['all']);

  // Main admins are protected: they must never appear in the Accounts
  // list so the trash icon can't be tapped against them. Managed
  // instead from the Governance › Main Admins tab (with a two-person
  // email-confirm removal flow).
  const isMainAdminUser = (user) => Boolean(user?.is_main_admin);
  const nonMainAdminUsers = useMemo(
    () => users.filter((entry) => !isMainAdminUser(entry)),
    [users],
  );
  const filteredSourceUsers = getUserResults(searchQuery, searchResults, users)
    .filter((entry) => !isMainAdminUser(entry));
  const roleCounts = useMemo(() => ({
    all: nonMainAdminUsers.length,
    guest: nonMainAdminUsers.filter((entry) => entry.role === 'guest').length,
    host: nonMainAdminUsers.filter((entry) => entry.role === 'host' || Boolean(entry.host_status)).length,
    staff: nonMainAdminUsers.filter((entry) => entry.role === 'staff').length,
    owner: nonMainAdminUsers.filter((entry) => entry.role === 'venue_owner' || Boolean(entry.venue_status)).length,
    admin: nonMainAdminUsers.filter((entry) => entry.role === 'admin').length,
    deleted: nonMainAdminUsers.filter(isDeletedAccount).length,
  }), [nonMainAdminUsers]);

  const visibleUsers = useMemo(() => {
    // "Deleted" is an opt-in filter — never bundled into "All" so the
    // default view stays focused on accounts the admin can still act on.
    if (selectedRoleFilters.includes('deleted')) {
      return filteredSourceUsers.filter(isDeletedAccount);
    }
    const liveUsers = filteredSourceUsers.filter((entry) => !isDeletedAccount(entry));
    if (selectedRoleFilters.includes('all')) {
      return liveUsers;
    }
    return liveUsers.filter((entry) => {
      if (selectedRoleFilters.includes('guest') && entry.role === 'guest') return true;
      if (selectedRoleFilters.includes('host') && (entry.role === 'host' || Boolean(entry.host_status))) return true;
      if (selectedRoleFilters.includes('staff') && entry.role === 'staff') return true;
      if (selectedRoleFilters.includes('owner') && (entry.role === 'venue_owner' || Boolean(entry.venue_status))) return true;
      if (selectedRoleFilters.includes('admin') && entry.role === 'admin') return true;
      return false;
    });
  }, [filteredSourceUsers, selectedRoleFilters]);

  if (selectedGuest) {
    return (
      <AdminGuestDetailView
        detail={guestDetail}
        loading={guestDetailLoading}
        onBack={() => { setSelectedGuest(null); setGuestDetail(null); }}
        onRemoveQr={onRemoveQr}
        onDeleteReview={onDeleteReview}
        onStartConversation={onStartConversation}
        onRefresh={() => onFetchGuestDetail(selectedGuest)}
      />
    );
  }

  if (selectedHost) {
    return (
      <AdminHostDetailView
        detail={hostDetail}
        loading={hostDetailLoading}
        backLabel="Back"
        onBack={() => { setSelectedHost(null); setHostDetail(null); }}
        onStartConversation={onStartConversation}
        onRefresh={() => onFetchHostDetail(selectedHost)}
      />
    );
  }

  return (
    <div>
      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240, display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #E7E7E7', borderRadius: 14, padding: '12px 14px' }}>
            <Search size={18} color="#666" />
            <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') onSaveRecentSearch(searchQuery); }} data-testid="admin-search-input" placeholder="Search by e-mail or Account ID" style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 15 }} />
            <button onClick={() => onSaveRecentSearch(searchQuery)} data-testid="admin-search-button" style={{ border: 'none', background: '#111', color: '#FFF', borderRadius: 10, padding: '8px 10px', cursor: 'pointer' }}>
              <Search size={14} />
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 14 }}>
          {accountFilterOptions.map((filterOption) => (
            <button key={filterOption.id} onClick={() => setSelectedRoleFilters((current) => toggleSelectableFilters(current, filterOption.id))} data-testid={`admin-filter-${filterOption.id}`} style={{ padding: '8px 12px', borderRadius: 14, border: '1px solid #E7E7E7', cursor: 'pointer', fontSize: 12, fontWeight: 700, background: selectedRoleFilters.includes(filterOption.id) ? '#111' : '#FFF', color: selectedRoleFilters.includes(filterOption.id) ? '#FFF' : '#555' }}>
              {filterOption.label} ({roleCounts[filterOption.id] || 0})
            </button>
          ))}
        </div>
        <p style={{ margin: '12px 0 0', fontSize: 13, color: '#666' }}>One account keeps one shared Account ID across guest, host, and venue access.</p>
        {recentSearches.length ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {recentSearches.map((entry) => (
              <button key={entry} onClick={() => setSearchQuery(entry)} data-testid={`admin-recent-search-${entry}`} style={{ padding: '6px 10px', borderRadius: 9999, border: '1px solid #E7E7E7', background: '#FFF', cursor: 'pointer', fontSize: 12, color: '#444' }}>
                {entry}
              </button>
            ))}
          </div>
        ) : null}
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {visibleUsers.map((entry) => {
          const canOpenHost = entry.role === 'host' || Boolean(entry.host_status);
          const venueApp = venueApps.find((app) => app.user?.id === entry.id || app.user_id === entry.id);
          const canOpenVenue = Boolean(entry.venue_status) || Boolean(venueApp);
          const isFinalised = isFinalisedDeletion(entry);
          const isPendingDeletion = !isFinalised && Boolean(entry.deletion_pending);
          const accountIsDeleted = isFinalised || isPendingDeletion;
          const finaliseAt = entry.deletion_finalize_at ? new Date(entry.deletion_finalize_at) : null;
          const finaliseLabel = finaliseAt && !Number.isNaN(finaliseAt.getTime())
            ? finaliseAt.toLocaleDateString('en-GB', { dateStyle: 'medium' })
            : null;

          return (
            <Card
              key={entry.id}
              data-testid={`admin-account-card-${entry.id}`}
              style={{
                gap: 14,
                display: 'grid',
                opacity: isFinalised ? 0.72 : 1,
                background: isFinalised ? '#F6F7F9' : isPendingDeletion ? '#FFF7EC' : undefined,
                border: isFinalised
                  ? '1px solid rgba(15,17,21,0.12)'
                  : isPendingDeletion
                    ? '1px solid rgba(214, 137, 16, 0.32)'
                    : undefined,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                <div>
                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 20, fontWeight: 700, margin: 0 }}>{entry.name || entry.username}</p>
                  <p style={{ fontSize: 13, color: '#666', margin: '4px 0 0' }}>{entry.email}</p>
                  <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: '#111', margin: '8px 0 0' }}>Account ID: {entry.customer_id || '—'}</p>
                  <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                    {isFinalised ? (
                      <Badge text="deleted · anonymised" color="red" data-testid={`admin-account-deleted-badge-${entry.id}`} />
                    ) : isPendingDeletion ? (
                      <Badge text={finaliseLabel ? `deletion pending · finalises ${finaliseLabel}` : 'deletion pending'} color="orange" data-testid={`admin-account-pending-deletion-badge-${entry.id}`} />
                    ) : (
                      <Badge text={`active · ${entry.role}`} color={entry.role === 'admin' ? 'red' : entry.role === 'host' ? 'blue' : 'green'} />
                    )}
                    {!isFinalised && entry.host_status ? <Badge text={`host: ${entry.host_status}`} color={entry.host_status === 'approved' ? 'green' : entry.host_status === 'rejected' ? 'red' : 'blue'} /> : null}
                    {!isFinalised && entry.venue_status ? <Badge text={`owner: ${entry.venue_status}`} color={entry.venue_status === 'approved' ? 'green' : entry.venue_status === 'on_hold' ? 'orange' : 'blue'} /> : null}
                  </div>
                  {isPendingDeletion ? (
                    <p data-testid={`admin-account-pending-deletion-hint-${entry.id}`} style={{ margin: '8px 0 0', fontSize: 12, color: '#7A5613', lineHeight: 1.45 }}>
                      Owner requested account deletion. The grace window keeps the account usable so they can cancel from their profile; anonymisation runs automatically at the date above.
                    </p>
                  ) : null}
                  {isFinalised ? (
                    <p data-testid={`admin-account-deleted-hint-${entry.id}`} style={{ margin: '8px 0 0', fontSize: 12, color: '#6B7280', lineHeight: 1.45 }}>
                      This account has been anonymised. PII was stripped, reviews show "Former Allure user", login is disabled. Financial records are retained for tax/PSD2.
                    </p>
                  ) : null}
                  <TimelinePreview
                    timelineId={`account-${entry.id}`}
                    expandedTimelineId={expandedTimelineId}
                    setExpandedTimelineId={setExpandedTimelineId}
                    items={[
                      { label: 'Joined', value: formatEuropeanDateTime(entry.created_at) },
                      { label: 'Current role', value: entry.role },
                      { label: 'Account ID', value: entry.customer_id || '—' },
                    ]}
                  />
                </div>
                {!isFinalised ? (
                  <button onClick={(event) => { event.stopPropagation(); onDeleteUser(entry.id); }} data-testid={`admin-delete-user-${entry.id}`} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><Trash2 size={16} color="#CCC" /></button>
                ) : null}
              </div>
              {accountIsDeleted ? null : (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => onFetchGuestDetail(entry.id)} data-testid={`manage-guest-btn-${entry.id}`} style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid #007AFF', background: '#FFFFFF', color: '#007AFF', cursor: 'pointer', fontWeight: 700 }}>GUEST</button>
                  {canOpenHost ? <button onClick={() => onFetchHostDetail(entry.id)} data-testid={`manage-host-btn-${entry.id}`} style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid #FF9500', background: '#FFFFFF', color: '#FF9500', cursor: 'pointer', fontWeight: 700 }}>HOST</button> : null}
                  {canOpenVenue ? <button onClick={() => { if (venueApp) { setSelectedVenueApp(venueApp); setActiveTab('venue-apps'); } }} data-testid={`manage-venue-btn-${entry.id}`} style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid #FF3B30', background: '#FFFFFF', color: '#FF3B30', cursor: 'pointer', fontWeight: 700 }}>VENUE</button> : null}
                  <button onClick={() => onContactUser(entry.id)} data-testid={`admin-account-contact-user-${entry.id}`} style={{ marginLeft: 'auto', padding: '10px 14px', borderRadius: 12, border: '1px solid var(--card-border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', fontWeight: 700 }}>Contact user</button>
                </div>
              )}
            </Card>
          );
        })}
        {!visibleUsers.length ? (
          <Card data-testid="admin-accounts-empty-state"><p style={{ margin: 0, color: '#666' }}>{selectedRoleFilters.includes('deleted') ? 'No deleted accounts. Anonymised accounts (and any pending-deletion requests) will appear here.' : 'No matching accounts found.'}</p></Card>
        ) : null}
      </div>
    </div>
  );
};